import { FC, useMemo, useState, useCallback } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { Loader2 } from "lucide-react"

import Accordion from "../../accordion"
import { useFetchPricingLogCountQuery } from "../../../../redux/queries"
import { groupPricingLogCountByMonth } from "../../../utils"
import { getStackedColumnChartOptions } from "./chart-options"

export interface ChartClickData {
  period: string
  viewType: "daily" | "monthly"
  status: "success" | "failed"
  category: string
  index: number
}

interface Props {
  orgId?: string
  brandId?: string
  onChartClick?: (data: ChartClickData) => void
  selectedBarIndex?: number | null
}

type ViewType = "daily" | "monthly"

const MonthlyStats: FC<Props> = ({
  orgId,
  brandId,
  onChartClick,
  selectedBarIndex
}) => {
  const [viewType, setViewType] = useState<ViewType>("monthly")

  // Determine range based on view type
  const range = viewType === "daily" ? 90 : 360

  const { data: allLogCounts = [], isFetching: isFetchingAllLogCounts } =
    useFetchPricingLogCountQuery({
      range,
      org_id: orgId,
      brand_id: brandId
    })
  const {
    data: successLogCounts = [],
    isFetching: isFetchingSuccessLogCounts
  } = useFetchPricingLogCountQuery({
    range,
    org_id: orgId,
    brand_id: brandId,
    success_only: 1
  })
  const { data: failedLogCounts = [], isFetching: isFetchingFailedLogCounts } =
    useFetchPricingLogCountQuery({
      range,
      org_id: orgId,
      brand_id: brandId,
      failed_only: 1
    })

  const isPricingLogCountsLoading =
    isFetchingAllLogCounts ||
    isFetchingSuccessLogCounts ||
    isFetchingFailedLogCounts

  // Transform data for daily view
  const dailyChartData = useMemo(() => {
    if (isPricingLogCountsLoading || viewType !== "daily") return null

    // Create a map for quick lookups
    const successMap = new Map(
      successLogCounts.map(item => [item.period_start, item.pricing_log_count])
    )
    const failedMap = new Map(
      failedLogCounts.map(item => [item.period_start, item.pricing_log_count])
    )

    // Get all unique dates and sort them
    const allDates = Array.from(
      new Set([
        ...allLogCounts.map(item => item.period_start),
        ...successLogCounts.map(item => item.period_start),
        ...failedLogCounts.map(item => item.period_start)
      ])
    ).sort()

    // Build arrays for chart
    const categories = allDates.map(date => {
      // Format date as "MMM DD" (e.g., "Jan 15")
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      })
    })

    const successData = allDates.map(date => successMap.get(date) || 0)
    const failedData = allDates.map(date => failedMap.get(date) || 0)

    return {
      categories,
      successData,
      failedData,
      periods: allDates // Store original ISO date strings
    }
  }, [
    isPricingLogCountsLoading,
    viewType,
    allLogCounts,
    successLogCounts,
    failedLogCounts
  ])

  // Transform data for monthly view
  const monthlyChartData = useMemo(() => {
    if (isPricingLogCountsLoading || viewType !== "monthly") return null

    const monthlyData = groupPricingLogCountByMonth(
      allLogCounts,
      successLogCounts,
      failedLogCounts
    )

    // Build arrays for chart
    const categories = monthlyData.map(item => `${item.month} ${item.year}`)
    const successData = monthlyData.map(item => item.successCount)
    const failedData = monthlyData.map(item => item.failedCount)
    const periods = monthlyData.map(item => `${item.year}-${item.month}`)

    return {
      categories,
      successData,
      failedData,
      periods // Store year-month strings like "2025-January"
    }
  }, [
    isPricingLogCountsLoading,
    viewType,
    allLogCounts,
    successLogCounts,
    failedLogCounts
  ])

  // Get chart data based on view type
  const chartData = viewType === "daily" ? dailyChartData : monthlyChartData

  // Handle chart bar click
  const handlePointClick = useCallback(
    (index: number, seriesName: string) => {
      if (!chartData || !onChartClick) return

      const period = chartData.periods[index]
      const category = chartData.categories[index]
      const status = seriesName === "Successful Calls" ? "success" : "failed"

      onChartClick({
        period,
        viewType,
        status,
        category,
        index
      })
    },
    [chartData, onChartClick, viewType]
  )

  // Generate chart options
  const chartOptions = useMemo(() => {
    if (!chartData) return null

    return getStackedColumnChartOptions(
      chartData.categories,
      chartData.successData,
      chartData.failedData,
      viewType,
      handlePointClick,
      selectedBarIndex
    )
  }, [chartData, viewType, selectedBarIndex, handlePointClick])

  const hasData =
    chartData &&
    (chartData.successData.some(v => v > 0) ||
      chartData.failedData.some(v => v > 0))

  return (
    <div className="flex flex-col flex-1 mt-2 mb-4 overflow-hidden">
      <Accordion
        title="Monthly Pricing Calls"
        childrenClassName="px-6 pb-4"
        noBorder={true}
      >
        {/* View Toggle Buttons */}
        <div className="flex gap-2 mt-4 mb-4">
          <button
            type="button"
            onClick={() => setViewType("daily")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "daily"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Daily
          </button>
          <button
            type="button"
            onClick={() => setViewType("monthly")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "monthly"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Chart Container */}
        <div className="bg-white rounded-md relative">
          {isPricingLogCountsLoading && (
            <div className="flex justify-center items-center py-20">
              <Loader2
                className="animate-spin h-12 w-12 text-primary-alt-400"
                aria-hidden="true"
              />
            </div>
          )}

          {!isPricingLogCountsLoading && !hasData && (
            <div className="flex justify-center items-center py-20">
              <p className="text-gray-500 text-sm">No data available</p>
            </div>
          )}

          {!isPricingLogCountsLoading && hasData && chartOptions && (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          )}
        </div>
      </Accordion>
    </div>
  )
}

export default MonthlyStats
